import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { emApp, ehCelular, tentarInstalar } from '@/lib/app';
import { useModoEquipe } from '@/components/feature/ModoEquipe';
import {
  suportaAvisos,
  estadoPermissao,
  ligarAvisosGerais,
  reassinarEmSilencio,
  precisaInstalarNoIphone,
} from '@/lib/avisos';
import { mostrarToast } from '@/lib/formularioGuiado';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { useVezDeFolha } from '@/lib/filaFolhas';
import { usePedidoUnico, adiarAvisos } from '@/lib/pedidosApp';
import Sheet from '@/components/ui/Sheet';
import PrimaryButton from '@/components/ui/PrimaryButton';
import FolhaColocarNaTela from './FolhaColocarNaTela';
import FolhaAbrirNavegador from './FolhaAbrirNavegador';

type Modo = 'pedir' | 'negado' | 'iphone';

// ---------------------------------------------------------------------------
// "Ative os avisos do Se Liga" — folha que sobe (Sheet) SEMPRE que a pessoa
// abre o app logada (instalado ou no navegador do celular), enquanto a permissão
// de notificação ainda não estiver liberada. Também reaparece quando o app volta
// para frente (visibilitychange), se ainda não estiver liberado.
//
// O "Agora não" fecha só até a próxima abertura (não há mais soneca de dias).
// O pedido do navegador (Notification.requestPermission) só é chamado quando a
// pessoa toca em "Ativar avisos" — nunca sozinho.
//
// Além da folha, este componente SEMPRE roda em segundo plano: a cada abertura
// logado, se a permissão já estiver concedida, reassina em silêncio para manter
// a assinatura viva e presa à pessoa (mesmo se ela trocou de conta no aparelho).
// ---------------------------------------------------------------------------
export default function ConviteAvisos() {
  const { ativo: modoEquipe } = useModoEquipe();
  const { pathname, search } = useLocation();
  const [logado, setLogado] = useState(false);
  const [telaEstreita, setTelaEstreita] = useState(false);
  const [modo, setModo] = useState<Modo | null>(null);
  const [dispensado, setDispensado] = useState(false);
  const [pedindo, setPedindo] = useState(false);
  const [folhaColocar, setFolhaColocar] = useState(false);
  const [folhaAbrir, setFolhaAbrir] = useState(false);

  // Vale para app instalado OU navegador de celular. A largura estreita cobre
  // a pré-visualização no modo celular.
  const aquiNoCelular = emApp() || ehCelular() || telaEstreita;

  // Não pede avisos na inscrição nem no evento aberto por link (?c=).
  // Continua normal na tela da inscrição concluída (/minha-inscricao/...).
  const naoPedirAqui =
    pathname.startsWith('/inscricao/') ||
    (/^\/e\/[^/]+$/.test(pathname) && search.includes('c='));

  // Observa a sessão (some/aparece quando entra ou sai).
  useEffect(() => {
    let vivo = true;
    supabase.auth.getSession().then(({ data }) => {
      if (vivo) setLogado(Boolean(data.session?.user));
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_evento, session) => {
      setLogado(Boolean(session?.user));
    });
    return () => {
      vivo = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Tela estreita (celular / prévia em modo celular).
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const aplicar = () => setTelaEstreita(mq.matches);
    aplicar();
    mq.addEventListener('change', aplicar);
    return () => mq.removeEventListener('change', aplicar);
  }, []);

  // Decide qual folha mostrar (ou nenhuma).
  const calcular = useCallback((): Modo | null => {
    if (!logado || modoEquipe || !aquiNoCelular || naoPedirAqui) return null;
    // iPhone só recebe avisos com o app na tela inicial (iOS 16.4+).
    if (precisaInstalarNoIphone()) return 'iphone';
    if (!suportaAvisos()) return null;
    const perm = estadoPermissao();
    if (perm === 'granted') return null;
    if (perm === 'denied') return 'negado';
    return 'pedir';
  }, [logado, modoEquipe, aquiNoCelular, naoPedirAqui]);

  // A cada abertura logado com permissão já dada: reassina em silêncio.
  useEffect(() => {
    if (!logado) return;
    if (estadoPermissao() !== 'granted') return;
    void reassinarEmSilencio();
  }, [logado]);

  // Recalcula a folha quando o estado relevante muda.
  useEffect(() => {
    if (dispensado) {
      setModo(null);
      return;
    }
    setModo(calcular());
  }, [calcular, dispensado]);

  // Ao voltar para o app (visibilitychange): reassina em silêncio se já liberado
  // e reapresenta a folha se ainda não estiver liberado.
  useEffect(() => {
    const aoVoltar = () => {
      if (document.visibilityState !== 'visible') return;
      if (estadoPermissao() === 'granted') {
        void reassinarEmSilencio();
        setModo(null);
        return;
      }
      if (!dispensado) setModo(calcular());
    };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => document.removeEventListener('visibilitychange', aoVoltar);
  }, [calcular, dispensado]);

  // Fila de folhas de abertura: a de avisos é a 2ª (depois de instalar o app).
  const minhaVez = useVezDeFolha('convite-avisos', 2, modo !== null);

  // UM pedido por vez: nada nos primeiros 20 s, nada antes de rolar a página,
  // nada na primeira abertura do dia e no máximo 1 pedido por sessão.
  const podePedir = usePedidoUnico(
    'avisos',
    modo !== null && !dispensado,
    2,
  );

  // Pede a permissão (só depois do toque) e liga os avisos gerais da pessoa.
  async function ligar() {
    setPedindo(true);
    try {
      await ligarAvisosGerais();
      setModo(null);
      mostrarToast('Pronto! Avisos ligados.', 5000, 'sucesso');
    } catch (e) {
      const perm = estadoPermissao();
      if (perm === 'granted') {
        setModo(null);
      } else if (perm === 'denied') setModo('negado');
      mostrarToast(mensagemAmigavel(e, 'Não conseguimos ligar os avisos.'), 6000, 'erro');
    } finally {
      setPedindo(false);
    }
  }

  // iPhone sem o app instalado: abre o passo a passo para adicionar à Tela de Início.
  function instalar() {
    const resultado = tentarInstalar();
    if (resultado === 'prompt') return;
    if (resultado === 'ios') {
      setFolhaColocar(true);
      return;
    }
    if (resultado === 'ios-outro') {
      setFolhaAbrir(true);
      return;
    }
    mostrarToast('Toque em Compartilhar › Adicionar à Tela de Início.');
  }

  function agoraNao() {
    setDispensado(true);
    setModo(null);
    // "Agora não" esconde por 7 dias.
    adiarAvisos();
  }

  if (!modo) return null;
  if (!podePedir) return null;
  if (!minhaVez) return null;

  const titulo = 'Ative os avisos do Se Liga';

  let corpo: string;
  let acao: () => void;
  let rotulo: string;
  let icone: string;

  if (modo === 'iphone') {
    corpo =
      'No iPhone, instale o app (Compartilhar › Adicionar à Tela de Início) para receber os avisos no celular.';
    acao = instalar;
    rotulo = 'Como instalar';
    icone = 'ri-download-2-line';
  } else if (modo === 'negado') {
    corpo =
      'Os avisos estão bloqueados neste celular. Toque no cadeado/ⓘ ao lado do endereço (ou em Configurações › Apps › Se Liga › Notificações) e permita.';
    acao = ligar;
    rotulo = pedindo ? 'Conferindo…' : 'Já liberei';
    icone = 'ri-lock-unlock-line';
  } else {
    corpo = 'Ingresso, fichas, recados da equipe e novidades dos eventos — na hora.';
    acao = ligar;
    rotulo = pedindo ? 'Ligando…' : 'Ativar avisos';
    icone = 'ri-notification-3-line';
  }

  return (
    <>
      <Sheet aberto aoFechar={agoraNao}>
        <div className="flex flex-col items-center pt-2 text-center">
          <span className="flex h-16 w-16 items-center justify-center text-primary-500">
            <i className={`${icone} text-[44px]`} aria-hidden />
          </span>
          <h2 className="mt-3 text-[20px] font-extrabold leading-tight text-foreground-950">
            {titulo}
          </h2>
          <p className="mt-2 max-w-sm text-[15px] leading-relaxed text-foreground-600">{corpo}</p>
        </div>

        <PrimaryButton cheio className="mt-6" icone={icone} carregando={pedindo} onClick={acao}>
          {rotulo}
        </PrimaryButton>
        <button
          type="button"
          onClick={agoraNao}
          className="aperta mx-auto mt-2 block cursor-pointer whitespace-nowrap py-2.5 text-[15px] font-semibold text-foreground-500"
        >
          Agora não
        </button>
      </Sheet>

      {folhaColocar && <FolhaColocarNaTela aoFechar={() => setFolhaColocar(false)} />}
      {folhaAbrir && <FolhaAbrirNavegador alvo="ios" aoFechar={() => setFolhaAbrir(false)} />}
    </>
  );
}